export class RollingBitmapNumberView extends PIXI.Container {
    protected _config;
    protected _digits: PIXI.Container[];
    protected _digitTweens: gsap.TweenMax[];
    protected _digitWidth: number;
    protected _digitHeight: number;
    protected _startPosition: number;
    protected _leftPadding: number;
    protected _dotTextField: PIXI.extras.BitmapText; // Container or text??
    protected _diamondBitmap: PIXI.Sprite;
    protected _finalValue: number;
    protected _currValue: number;
    protected _finalCentValue: number;
    protected _delta: number;
    protected _direction: number;
    protected _oldDirection: number;
    protected _timePerCent: number;
    protected _totalTime: number;
    protected _dummyObject;
    protected _failSafeTween: gsap.TweenMax;
    protected _numberOfSeparators: number;
    protected _storedMask: PIXI.Graphics;

    constructor(config: object) {
        super();

        this._finalCentValue = this._finalValue = 0;
        this._currValue = 0;
        this._finalValue = 0;
        this._delta = 0;
        this._direction = 0;
        this._oldDirection = 0;
        this._timePerCent = 0;
        this._dummyObject = { value: 0 };

        this._digits = [];
        this._digitTweens = [];

        this.config = config;
    }

    public isAnimating(): boolean {
        const len = this._digitTweens.length;
        for (let i = 0; i < len; i++) {
            if (this._digitTweens[i]) {
                return true;
            }
        }

        if (this._failSafeTween) {
            return true;
        }
        return false;
    }

    public denomination(value: number) {
        this._config.denomination = value;
        this.currency(this._config.currency);
    }

    public killTweens(): void {
        const len = this._digitTweens.length;
        for (let i = 0; i < len; i++) {
            if (this._digitTweens[i]) {
                this._digitTweens[i].kill();
            }
            this._digitTweens[i] = null;
        }
        if (this._failSafeTween) {
            this._finalValue = this._dummyObject.finalValue;
            this._failSafeTween.kill();
        }
        this._failSafeTween = null;
    }

    public currency(val: boolean): void {
        this._config.currency = val;
        this.killTweens();
        this.createNumbers();
        this.setValue(this._finalCentValue);
    }

    setBounds(value: any, shouldTweensContinue = false): void {
        this._config.bounds = { width: value.width, height: value.height };

        let isThereFailSafeTween = false

        if(shouldTweensContinue && !!this._failSafeTween){
            isThereFailSafeTween = true
            this._currValue = this._dummyObject.value;
            this._finalValue = this._dummyObject.finalValue;
            this._totalTime -= this._failSafeTween.time()
        }

        this.killTweens();
        this.createNumbers();
        this.createMask();
        this.setFinalValue();

        if(shouldTweensContinue && isThereFailSafeTween && this._totalTime > 0){
            this._currValue = this._dummyObject.value;
            this.setValue(this._finalValue, true, this._totalTime)
        }
    }

    set config(value: any) {
        this._config = value;

        if (!this._config.separatorSize) {
            this._config.separatorSize = 8;
        }

        if (this._config.topPadding === undefined) {
            this._config.topPadding = 0;
        }

        if (this._config.denomination === undefined) {
            this._config.denomination = 1;
        }

        if (this._config.horizontalSpacing === undefined) {
            this._config.horizontalSpacing = 0;
        }

        let shouldTweensContinue = false
        if(value.shouldTweensContinue && !!this._failSafeTween){
            shouldTweensContinue = true
            this._currValue = this._dummyObject.value;
            this._finalValue = this._dummyObject.finalValue;
            this._totalTime -= this._failSafeTween.time()
        }

        this.killTweens();
        this.createNumbers();
        this.createMask();
        this.setFinalValue();

        if(value.shouldTweensContinue && shouldTweensContinue && this._totalTime > 0){
            this._currValue = this._dummyObject.value;
            this.setValue(this._finalValue, true, this._totalTime)
        }
    }

    public setValue(value: number, animating?: boolean, time: number = 0) {
        this._finalCentValue = value;
        if (this._config.isDiamondPlay) {
            value = Math.floor(value / 100);
        } else if (!this._config.currency) {
            value = Math.floor(value / this._config.denomination);
        } else if (this._config.numberOfCents === 0) {
            value = Math.floor(value / 100);
        }
        this._finalValue = value;
        this._totalTime = time;

        if (animating) {
            this._delta = this._currValue - value;
            if (this._delta === 0) {
                return;
            }

            const prevDir: number = this._direction;
            this._direction = this._delta < 0 ? -1 : 1;
            this._oldDirection = this._direction;
            this._timePerCent = Math.abs(time / this._delta);
            const anim = this.isAnimating();
            if (anim) {
                if (prevDir !== this._direction) {
                    this._oldDirection = prevDir;
                }
                if (this._failSafeTween) {
                    this._failSafeTween.kill();
                    this._failSafeTween = null;
                    this._finalValue = this._dummyObject.value;
                    this.setFinalValue();
                    this._finalValue = value;
                    this.animateDigit(0);
                }
            } else {
                this.animateDigit(0);
            }
        } else {
            const anim = this.isAnimating();
            if (anim) {
                gsap.TweenMax.to(this, 0.3, {
                    alpha: 0,
                    ease: gsap.Linear.easeNone,
                    onComplete: this.onFadeOutComplete.bind(this),
                });
            } else {
                this.setFinalValue();
            }
        }
    }

    public complete(): void {
        if (this.isAnimating) {
            gsap.TweenMax.to(this, 0.3, { alpha: 0, ease: gsap.Linear.easeNone, onComplete: this.onFadeOutComplete.bind(this) });
        }
    }

    protected createMask() {
        if (this._storedMask == null) {
            this._storedMask = new PIXI.Graphics();
            this.addChild(this._storedMask);

            if (!this._config.debug) {
                this.mask = this._storedMask;
            }
        }

        this._storedMask.clear();
        this._storedMask.beginFill(0xff0000, 0.5);
        this._storedMask.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
        this._storedMask.endFill();
    }

    protected animateDigit(index: number) {
        if (index === this._digits.length) {
            return;
        }
        if (this._digitTweens[index]) {
            this._digitTweens[index].totalTime(this._digitTweens[index].totalDuration());
        }

        // TODO: should be < 0.020 test it
        if (this._timePerCent < 0.04 || this._direction === 1) {
            this._dummyObject.value = this._currValue;
            this._dummyObject.finalValue = this._finalValue;
            this._failSafeTween = gsap.TweenMax.to(this._dummyObject, this._totalTime,
                {
                    value: this._finalValue, ease: gsap.Linear.easeNone, roundProps: ["value"],
                    onUpdate: this.onFailSafeTweenUpdate, onUpdateScope: this, onCompleteScope: this, onComplete: function () {
                        this._failSafeTween = null;
                    },
                });
            return;
        }

        const digitSprite = this._digits[index];
        const currDigit = Number((digitSprite.children[0] as PIXI.extras.BitmapText).text);
        let nextDigit = currDigit - this._direction;

        if (this._direction === -1) {
            if (nextDigit > 9) {
                nextDigit = 0;
            }
            (digitSprite.children[1] as PIXI.extras.BitmapText).text = nextDigit.toString();
            digitSprite.visible = true;
        } else {
            // TODO: reverse animation should be checked
            if (nextDigit < 0) {
                nextDigit = 9;
            }
            digitSprite.y = -this._config.bounds.height;
            (digitSprite.children[1] as PIXI.extras.BitmapText).text = currDigit.toString();
            (digitSprite.children[0] as PIXI.extras.BitmapText).text = nextDigit.toString();
        }

        if ((this._direction === -1 && currDigit === 9) || (this._direction === 1 && currDigit === 0)) {
            this.animateDigit(index + 1);
        }

        this._digitTweens[index] = gsap.TweenMax.to(digitSprite, this._timePerCent,
            {
                y: digitSprite.y + this._config.bounds.height * this._direction,
                ease: gsap.Linear.easeNone,
                roundProps: ["y"],
                onComplete: this.onCentAnimationComplete as (() => void),
                callbackScope: this,
                onCompleteParams: [index],
            });

    }

    protected setFinalValue() {
        this._currValue = this._finalValue;
        let value = this._finalValue;
        const len = this._digits.length;
        if (value > Math.pow(10, len) - 1) {
            value = value % Math.pow(10, len);
        }

        const strValue = value.toString();
        const strLen = strValue.length;

        for (let i = len - 1; i >= 0; i--) {
            this._digits[i].y = this._startPosition;
            this._digits[i].visible = true;
            if (i >= strLen) {
                (this._digits[i].children[0] as PIXI.Text).text = "0";
                // TODO is it needed
                if (!this._config.currency || i > this._config.numberOfCents) {
                    this._digits[i].visible = false;
                }
            } else {
                (this._digits[i].children[0] as PIXI.Text).text = strValue.charAt(strLen - 1 - i);
            }
        }
        this.alpha = 1;
    }

    protected onCentAnimationComplete(index) {
        this._digitTweens[index] = null;
        const digitSprite = this._digits[index];
        if (this._oldDirection === -1) {
            digitSprite.y = this._startPosition;
            (digitSprite.children[0] as PIXI.Text).text = (digitSprite.children[1] as PIXI.extras.BitmapText).text;
        }

        if (index === 0) {
            this._currValue = this._currValue - this._oldDirection;
            if (this._oldDirection === 1) {
                // let limit = this._config.currency && !this._config.isDiamondPlay ?
                // this._config.numberofCents+1 : 1;
                const limit = this._config.currency ? this._config.numberofCents + 1 : 1;
                for (let i = this._digits.length - 1; i >= limit; i--) {
                    if (!this._digits[i].visible) {
                        continue;
                    }
                    // if (parseInt((this._digits[i].children[0] as PIXI.Text).text) == 0) {
                    //     this._digits[i].visible = false;
                    //     break;
                    // } else {
                    //     break;
                    // }
                }
            }

            this._oldDirection = this._direction;
            if (this._currValue !== this._finalValue) {
                this.animateDigit(0);
            }
        }
    }

    protected onFailSafeTweenUpdate() {
        this._finalValue = this._dummyObject.value;
        this.setFinalValue();
    }

    protected onFadeOutComplete() {
        if (this._failSafeTween) {
            this._finalValue = this._dummyObject.finalValue;
        }
        this.killTweens();
        this.setFinalValue();
    }

    protected  createNumbers() {
        const digitsLength = this._digits.length;
        for (let i = 0; i < digitsLength; i++) {
            this.removeChild(this._digits[i]);
        }
        this._digits.length = 0;

        if (this._dotTextField != null) {
            this.removeChild(this._dotTextField);
            this._dotTextField = null;
        }

        if (this._config.currency && this._config.numberOfCents != 0) {
            this._dotTextField = new PIXI.extras.BitmapText(".", this._config.style);
        }

        const textField = new PIXI.extras.BitmapText("0", this._config.style);

        this._startPosition = Math.round((this._config.bounds.height - textField.textHeight) / 2 + this._config.topPadding);
        const count = this._config.digitsCount;
        const singleDigitWidth = textField.textWidth + this._config.horizontalSpacing;
        const dotSymbolWidth = this._dotTextField != null ? this._dotTextField.textWidth + this._config.horizontalSpacing : 0;

        const properCountOfNumbers = count - (this._config.currency ? this._config.numberOfCents : 0);
        const numberOfSeparators = Math.floor((properCountOfNumbers - 1) / 3);
        const totalSeparatorsWidth = this._config.currency ? numberOfSeparators * this._config.separatorSize : 0;

        const calculatedWidth = count * singleDigitWidth + (totalSeparatorsWidth - this._config.horizontalSpacing) + dotSymbolWidth;

        const startX = this._config.bounds.width - calculatedWidth;

        let sepCounter = properCountOfNumbers;
        let sepCount = 0;
        for (let i = 0; i < properCountOfNumbers; i++) {
            const cont = new PIXI.Container();
            for (let j = 0; j < 2; j++) {

                const txt = new PIXI.extras.BitmapText(j.toString(), this._config.style);
                cont.addChild(txt);
                if (j === 1) {
                    txt.position.y = this._config.bounds.height;
                }
            }
            if (sepCounter % 3 === 0 && i != 0 && this._config.currency) {
                sepCount++;
            }

            cont.position.x = startX + i * singleDigitWidth + sepCount * this._config.separatorSize;
            cont.position.x = Math.round(cont.position.x);

            this._digits.unshift(cont);
            this.addChild(cont);
            sepCounter--;
        }

        if (this._dotTextField) {
            this._dotTextField.x = Math.round(startX + properCountOfNumbers * singleDigitWidth + sepCount * this._config.separatorSize);
            this._dotTextField.y = this._startPosition;

            this.addChild(this._dotTextField);

            for (let i = 0; i < this._config.numberOfCents; i++) {
                const cont = new PIXI.Container();
                for (let j = 0; j < 2; j++) {
                    const txt = new PIXI.extras.BitmapText(j.toString(), this._config.style);
                    cont.addChild(txt);

                    if (j === 1) {
                        txt.position.y = this._config.bounds.height;
                    }
                }
                cont.position.x = startX + properCountOfNumbers * singleDigitWidth + i * singleDigitWidth +
                    dotSymbolWidth + sepCount * this._config.separatorSize;
                this.addChild(cont);
                this._digits.unshift(cont);
                cont.position.x = Math.round(cont.position.x);
            }
        }
    }
}
